// Monaco setup + paim-filter language registration.
//
// Imports the lean editor.api so we don't pull in the JSON/TS/CSS/HTML
// language servers. We register our own language ("paim-filter") with
// a Monarch tokenizer and a completion provider for column names,
// built-in functions (with snippet placeholders), comparison operators,
// and logical keywords.
//
// Vite handles Monaco's web worker via the `?worker` import suffix —
// no separate plugin required.

// Lean entry: editor + standard contributions (suggest, hover, find,
// etc.) but no bundled languages — we register our own paim-filter.
// Saves ~2-3 MB off the main chunk vs. `import * as monaco from
// "monaco-editor"`. NB: `editor.api` alone is too lean; it omits the
// Suggest UI contribution, so the completion popup never renders.
import * as monaco from "monaco-editor/esm/vs/editor/edcore.main";
import EditorWorker from "monaco-editor/esm/vs/editor/editor.worker?worker";
import { loader } from "@monaco-editor/react";
// Types from the main package — values come from the lean entry above.
import type { editor as editorNs, Position } from "monaco-editor";

type Monaco = typeof monaco;

// One worker is enough; we don't use any of Monaco's bundled languages.
self.MonacoEnvironment = {
  getWorker() {
    return new EditorWorker();
  },
};

// Tell @monaco-editor/react to use this locally-installed monaco rather
// than fetching from a CDN. Module-side-effect so the config lands
// before any <Editor /> mounts.
loader.config({ monaco });

// Dev convenience: expose monaco on window so devtools sessions can
// drive the editor (e.g. monaco.editor.getEditors()[0].trigger(...)).
// Harmless in prod; just lets us debug suggestion behavior in real time.
if (typeof window !== "undefined") {
  (window as unknown as { monaco: typeof monaco }).monaco = monaco;
}

const LANGUAGE_ID = "paim-filter";

// Keep in sync with src/paim/count.py:_DEFAULT_COLUMNS. The editor
// only uses these for completion; column-existence is enforced
// server-side by the filter compiler.
const COLUMNS = [
  "source_id",
  "source_name",
  "source_type",
  "station_id",
  "media_type",
  "market_id",
  "market_name",
  "market_country",
  "market_state",
  "latitude",
  "longitude",
  "dma_rank",
  "program_id",
  "schedule_id",
  "program_name",
  "categories",
  "break_type",
  "start_local",
  "start_utc",
  "line_count",
  "duration_sec",
  "n_ads",
  // Podcast-native fields (source_type == 'omcs-podcast'). Broadcast-only and
  // podcast-only columns coexist here — a filter is validated against the
  // union; union_by_name null-fills the columns a given content type lacks.
  "source_url",
  "source_priority",
  "episode_url",
  "asset_archive_url",
  "language",
  "explicit",
  "asset_pubdate",
];

interface FunctionDef {
  name: string;
  signature: string;
  snippet: string;
  doc: string;
}

const FUNCTIONS: FunctionDef[] = [
  {
    name: "contains",
    signature: "contains(col, 'sub')",
    snippet: "contains(${1:col}, '${2:sub}')",
    doc: "True if `col` contains the substring `'sub'`.",
  },
  {
    name: "has",
    signature: "has(list_col, 'value')",
    snippet: "has(${1:categories}, '${2:value}')",
    doc: "True if the list-valued `list_col` (e.g. `categories`) contains `'value'`.",
  },
  {
    name: "starts_with",
    signature: "starts_with(col, 'pre')",
    snippet: "starts_with(${1:col}, '${2:pre}')",
    doc: "True if `col` starts with `'pre'`.",
  },
  {
    name: "ends_with",
    signature: "ends_with(col, 'suf')",
    snippet: "ends_with(${1:col}, '${2:suf}')",
    doc: "True if `col` ends with `'suf'`.",
  },
  {
    name: "is_null",
    signature: "is_null(col)",
    snippet: "is_null(${1:col})",
    doc: "True when `col` is NULL.",
  },
  {
    name: "is_not_null",
    signature: "is_not_null(col)",
    snippet: "is_not_null(${1:col})",
    doc: "True when `col` is NOT NULL.",
  },
  {
    name: "lower",
    signature: "lower(col)",
    snippet: "lower(${1:col})",
    doc: "Lowercased value of `col` (use on either side of a comparison).",
  },
  {
    name: "upper",
    signature: "upper(col)",
    snippet: "upper(${1:col})",
    doc: "Uppercased value of `col`.",
  },
];

const KEYWORDS = ["and", "or", "not", "in"];
const COMPARISONS = ["==", "!=", "<", "<=", ">", ">="];

let registered = false;

export function setupFilterLanguage(m: Monaco) {
  if (registered) return;
  registered = true;

  m.languages.register({ id: LANGUAGE_ID });

  m.languages.setMonarchTokensProvider(LANGUAGE_ID, {
    keywords: KEYWORDS,
    builtinFunctions: FUNCTIONS.map((f) => f.name),
    operators: COMPARISONS,
    tokenizer: {
      root: [
        // identifier / keyword / function name
        [
          /[a-zA-Z_][\w$]*/,
          {
            cases: {
              "@keywords": "keyword",
              "@builtinFunctions": "type.identifier",
              "@default": "identifier",
            },
          },
        ],
        // strings (single or double quoted)
        [/'([^'\\]|\\.)*'/, "string"],
        [/"([^"\\]|\\.)*"/, "string"],
        // numbers
        [/\d+(\.\d+)?/, "number"],
        // operators
        [/==|!=|<=|>=|<|>/, "operator"],
        // delimiters
        [/[(),]/, "delimiter"],
        // whitespace
        [/\s+/, "white"],
      ],
    },
  });

  m.languages.setLanguageConfiguration(LANGUAGE_ID, {
    brackets: [["(", ")"]],
    autoClosingPairs: [
      { open: "(", close: ")" },
      { open: "'", close: "'" },
      { open: '"', close: '"' },
    ],
    surroundingPairs: [
      { open: "(", close: ")" },
      { open: "'", close: "'" },
      { open: '"', close: '"' },
    ],
  });

  m.languages.registerCompletionItemProvider(LANGUAGE_ID, {
    triggerCharacters: [" ", "(", ","],
    provideCompletionItems: (model: editorNs.ITextModel, position: Position) => {
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const columnSuggestions = COLUMNS.map((col) => ({
        label: col,
        kind: m.languages.CompletionItemKind.Field,
        insertText: col,
        detail: "column",
        range,
      }));

      const functionSuggestions = FUNCTIONS.map((fn) => ({
        label: { label: fn.name, detail: ` ${fn.signature}` },
        kind: m.languages.CompletionItemKind.Function,
        insertText: fn.snippet,
        insertTextRules: m.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: { value: fn.doc },
        detail: "function",
        range,
      }));

      const operatorSuggestions = COMPARISONS.map((op) => ({
        label: op,
        kind: m.languages.CompletionItemKind.Operator,
        insertText: op,
        detail: "operator",
        range,
      }));

      const keywordSuggestions = KEYWORDS.map((kw) => ({
        label: kw,
        kind: m.languages.CompletionItemKind.Keyword,
        insertText: kw,
        detail: "logical",
        range,
      }));

      return {
        suggestions: [
          ...columnSuggestions,
          ...functionSuggestions,
          ...operatorSuggestions,
          ...keywordSuggestions,
        ],
      };
    },
  });
}
